/**
 * Auth & Workspace API
 */
import type { AuthResponse, ItemResponse, ListResponse, DeleteResponse, User, Workspace } from '../../types/api.js';
import type { WorkspaceMember } from '../../types/models.js';
import { apiRequest } from './core.js';

/** Workspace entry as returned by GET /auth/me — includes the caller's role in it. */
export interface MeWorkspace {
  id: string;
  name: string;
  role: 'OWNER' | 'ADMIN' | 'EDITOR' | 'VIEWER';
  /**
   * Set when this membership carries a CustomRole, which REPLACES the default
   * role matrix server-side (rbacService.checkPermission short-circuits on it).
   * When it is set, `role` above decides nothing — any client-side permission
   * inference from `role` is invalid for this member and must fall back to
   * letting the server answer.
   */
  customRoleId?: string | null;
}

export const authApi = {
  register: (data: { email: string; password: string; name: string; workspaceName?: string }) =>
    apiRequest<AuthResponse>('/auth/register', { method: 'POST', body: JSON.stringify(data) }),
  login: (data: { email: string; password: string }) =>
    apiRequest<AuthResponse>('/auth/login', { method: 'POST', body: JSON.stringify(data) }),
  me: () => apiRequest<ItemResponse<{ user: User; workspaces: MeWorkspace[] }>>('/auth/me'),
  changePassword: (data: { currentPassword: string; newPassword: string }) =>
    apiRequest<ItemResponse<{ message: string }>>('/auth/change-password', { method: 'POST', body: JSON.stringify(data) }),
  refreshToken: (refreshToken: string) =>
    apiRequest<ItemResponse<{ token: string; refreshToken?: string }>>('/auth/refresh-token', { method: 'POST', body: JSON.stringify({ refreshToken }) })
};

export interface UserProfile {
  id: string;
  email: string;
  name: string;
  avatar?: string | null;
  /** UI language preference — only 'en' ships today (T9 scaffold). */
  locale?: string | null;
  /** Set once the first-run tour was finished or skipped (T10). */
  onboardingTourDismissedAt?: string | null;
  /** Kind-alignment §4: per-kind manually-taken setup step ids. */
  setupChecklistDone?: Record<string, string[]> | null;
  createdAt?: string;
}

export const userProfileApi = {
  get: () => apiRequest<ItemResponse<UserProfile>>('/users/profile'),
  update: (data: {
    name?: string;
    avatar?: string | null;
    locale?: string | null;
    tourDismissed?: true;
    /** Append one manually-taken setup step; merged server-side (§4). */
    setupStepDone?: { kind: string; stepId: string };
  }) =>
    apiRequest<ItemResponse<UserProfile>>('/users/profile', { method: 'PUT', body: JSON.stringify(data) }),
};

export const workspacesApi = {
  list: () => apiRequest<ListResponse<Workspace>>('/workspaces'),
  get: (id: string) => apiRequest<ItemResponse<Workspace>>(`/workspaces/${id}`),
  create: (data: { name: string; domain?: string; currency?: string; timezone?: string }) =>
    apiRequest<ItemResponse<Workspace>>('/workspaces', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: Partial<{ name: string; domain: string; currency: string; timezone: string; businessType: string }>) =>
    apiRequest<ItemResponse<Workspace>>(`/workspaces/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  inviteMember: (id: string, data: { email: string; role?: string }) =>
    apiRequest<ItemResponse<WorkspaceMember>>(`/workspaces/${id}/members`, { method: 'POST', body: JSON.stringify(data) }),
  removeMember: (id: string, memberId: string) =>
    apiRequest<DeleteResponse>(`/workspaces/${id}/members/${memberId}`, { method: 'DELETE' })
};
